import type { ChangeEvent, SelectHTMLAttributes } from 'react';
import { SearchableSelect } from './SearchableSelect.js';

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'children'> & {
  label?: string;
  hint?: string;
  error?: string;
  showErrorText?: boolean;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
    detail?: string | null;
    imageUrl?: string | null;
  }[];
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
};

/** Searchable dropdown — same API as native select, with filter-as-you-type panel. */
export function Select({
  label,
  hint,
  error,
  options,
  className,
  id,
  showErrorText = true,
  onChange,
  value,
  disabled,
  name,
  required,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: SelectProps) {
  const fieldId = id ?? name ?? (typeof rest['aria-labelledby'] === 'string' ? rest['aria-labelledby'] : undefined);
  const stringValue = value === undefined || value === null ? '' : String(value);

  const mappedOptions = options.map((o) => ({
    value: o.value,
    label: o.label,
    disabled: o.disabled,
    detail: o.detail,
    imageUrl: o.imageUrl,
  }));

  return (
    <SearchableSelect
      label={label}
      hint={hint}
      error={error}
      options={mappedOptions}
      value={stringValue}
      disabled={disabled}
      id={typeof fieldId === 'string' ? fieldId : undefined}
      className={className}
      showErrorText={showErrorText}
      listAriaLabel={label ?? 'Options'}
      searchPlaceholder="Rechercher…"
      required={required}
      aria-describedby={ariaDescribedBy}
      {...rest}
      onChange={(next) => {
        if (!onChange) return;
        const target = { value: next, name: name ?? '' };
        onChange({ target, currentTarget: target } as ChangeEvent<HTMLSelectElement>);
      }}
    />
  );
}
